'use client'

import type { Dispatch, ReactNode, SetStateAction, RefObject } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useRef, createContext, useContext, useEffect, useCallback } from 'react'
import { useOnClickOutside } from 'usehooks-ts'

const Modal = ({
	name,
	open,
	setOpen,
	children,
}: {
	name: string
	open: boolean
	setOpen: Dispatch<SetStateAction<boolean>>
	children: ReactNode
}) => {
	const containerRef = useRef<HTMLDivElement>(null)
	useOnClickOutside(containerRef as RefObject<HTMLElement>, () => {
		if (open) {
			setOpen(false)
		}
	})

	const handleEscape = useCallback((event: KeyboardEvent) => {
		if (event.key === 'Escape' && open) {
			setOpen(false)
		}
	}, [
		open,
		setOpen,
	])

	useEffect(() => {
		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [
		handleEscape,
	])

	return (
		<ModalContext.Provider
			value={{
				open,
				setOpen,
				toggleModal: () => {
					setOpen((prev) => {
						return !prev
					})
				},
				closeModal: () => {
					setOpen(false)
				},
				openModal: () => {
					setOpen(true)
				},
			}}
		>
			<AnimatePresence>
				{open ? (
					<motion.div
						key={name}
						role="dialog"
						aria-modal="true"
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
						exit={{
							opacity: 0,
						}}
						transition={{
							duration: 0.2,
						}}
						className="fixed top-0 left-0 w-full h-full md:backdrop-blur-lg bg-black/10 z-20"
					>
						<div className="flex justify-center items-center h-full w-full">
							<motion.div
								ref={containerRef}
								initial={{
									scale: 0.95,
								}}
								animate={{
									scale: 1,
								}}
								exit={{
									scale: 0.95,
								}}
								transition={{
									duration: 0.2,
								}}
								className="bg-gray-900 md:rounded-2xl px-2 py-5 md:px-5 fixed md:static top-0 left-0 w-full md:w-fit mx-auto h-full md:h-fit md:max-h-[80%] overflow-y-auto overscroll-contain"
							>
								{children}
							</motion.div>
						</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</ModalContext.Provider>
	)
}

type ModalContextValue = {
	open: boolean
	toggleModal: () => void
	openModal: () => void
	closeModal: () => void
	setOpen: Dispatch<SetStateAction<boolean>>
}

const ModalContext = createContext<ModalContextValue>({
	open: false,
	toggleModal: () => {},
	openModal: () => {},
	closeModal: () => {},
	setOpen: () => {},
})

export const useModal = () => {
	return useContext(ModalContext)
}

export default Modal
